import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Surface, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { NetworkLink } from '@app/router/constants';
import { CardWithActions } from '@app/components/CardWithActions';
import { useNavigate } from '@app/hooks';
import { selectConfig, selectNetwork, updateServiceSubType } from '@app/redux';

import { OntMode, UpdateCommandError } from '@mfe/shared/schema-types';

import { CommandState } from '../../../../../platform/src/server/dataSources/PSM/schemaTypes';
import { useFormValues } from '../network-management/useFormValues';
import { WifiForm } from '../network-management/wifi-forms';
import { GeneralErrorAlert } from './GeneralErrorAlert';
import { SubTypeUpdateLoading } from './SubTypeUpdateLoading';
import { SwitchAlert } from './SwitchAlert';

import styles from './styles.module.scss';

export const WifiMode = () => {
  const { t } = useTranslation('Network', { keyPrefix: 'networkManagement' });
  const dispatch = useDispatch();
  const { goTo, replace } = useNavigate();

  const { values, handlers, validation, loading, canSave } = useFormValues();

  const { enableBridgeMode } = useSelector(selectConfig);
  const { ontModeSwitchLoading, updateStatus, updateError } = useSelector(selectNetwork);

  const isGeneralError = updateError && updateError !== UpdateCommandError.RateLimitExceededError;

  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (!enableBridgeMode) {
      replace(NetworkLink.Network);
    }
  }, []);

  useEffect(() => {
    const hasSucceeded = updateStatus === CommandState.Succeeded;
    const hasExceededRateLimit = updateError === UpdateCommandError.RateLimitExceededError;

    if (hasSucceeded || hasExceededRateLimit) {
      goTo(NetworkLink.Network);
    }
  }, [updateStatus, isGeneralError]);

  return (
    <>
      <Surface my="64px" variant="secondary" className={styles.header}>
        <Txt variant="heading3" color="regular" data-cy="network-wifi-header">
          {t('switchToWifi.title')}
        </Txt>
      </Surface>

      {ontModeSwitchLoading ? (
        <SubTypeUpdateLoading />
      ) : (
        <Surface variant="secondary" className={styles.content}>
          {isGeneralError && <GeneralErrorAlert />}
          <SwitchAlert />
          <CardWithActions
            primaryActionLabel={t('switchToWifi.primaryAction')}
            primaryActionDisabled={!isChecked}
            handlePrimaryActionClick={() =>
              dispatch(
                updateServiceSubType({
                  ontMode: OntMode.WiFi,
                  wifiSsid: values.wifiName,
                  wifiPassphrase: values.retypePasswordValue,
                })
              )
            }
            secondaryActionLabel={t('switchToWifi.secondaryAction')}
          >
            <Txt variant="bodySmallRegular">{t('switchToWifi.description')}</Txt>
            <div>
              <WifiForm
                canSave={canSave}
                values={values}
                validation={validation}
                loading={loading}
                handlers={handlers}
              />
            </div>
            <Checkbox
              id="confirm-wifi-mode-switch"
              label={t('switchToWifi.confirmSwitch')}
              name="confirm-wifi-mode-switch"
              checked={isChecked}
              onChange={() => setIsChecked(!isChecked)}
            />
          </CardWithActions>
        </Surface>
      )}
    </>
  );
};
